import { ChangeDetectionStrategy, Component, OnInit, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { animate, style, transition, trigger } from '@angular/animations';
import { LocalizationModule } from '@abp/ng.core';
import { TooltipModule } from 'primeng/tooltip';
import { Badge } from 'primeng/badge';
import { NotificationStateService } from 'src/core/services/notification/notification-state.service';

const MAX_DISPLAYED_BADGE_COUNT = 99;
// Matches the badge's own resting transform (see notification-bell.component.scss) — each animation step
// must include it too, otherwise Angular's animated `transform` would momentarily override the badge back
// to the element's natural (unpositioned) top-left corner instead of its usual top-right-straddling spot.
const BADGE_REST_TRANSFORM = 'translate(50%, -50%)';

/**
 * Registered once as a LeptonX NavItem (see `app.component.ts`), so it's present on every authenticated
 * page — the bell icon + unread badge. Clicking it navigates to the full `/notifications` page (matching
 * the reference app's own UX) rather than opening a dropdown. All state (unread count, list, live push
 * handling) lives in `NotificationStateService`, shared with `NotificationMainComponent`.
 *
 * The badge is a `p-badge` used directly (not `p-overlayBadge`) and kept as a *sibling* of the bell icon,
 * not a wrapper around it — `p-overlayBadge` projects the icon inside its own root, so animating/toggling
 * the badge also touched the icon's DOM node and caused it to flicker. Positioning it ourselves also lets
 * us bind Angular's animation trigger directly to the badge element, which isn't possible for a nested
 * component's internal template.
 */
@Component({
  selector: 'app-notification-bell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterLink, LocalizationModule, TooltipModule, Badge],
  templateUrl: './notification-bell.component.html',
  styleUrl: './notification-bell.component.scss',
  animations: [
    trigger('badgePop', [
      transition('* => *', [
        style({ transform: `${BADGE_REST_TRANSFORM} scale(1)` }),
        animate('150ms ease-out', style({ transform: `${BADGE_REST_TRANSFORM} scale(1.35)` })),
        animate('150ms ease-out', style({ transform: `${BADGE_REST_TRANSFORM} scale(1)` })),
      ]),
    ]),
  ],
})
export class NotificationBellComponent implements OnInit {
  private readonly state = inject(NotificationStateService);

  private previousUnread: number | null = null;

  readonly countSummary = this.state.countSummary;
  /** Bound via `[@badgePop]` — only bumped when unread *increases*, so marking-as-read never pops it. */
  readonly popTrigger = signal(0);

  constructor() {
    effect(() => {
      const summary = this.countSummary();
      if (this.previousUnread !== null && summary.unread > this.previousUnread) {
        this.popTrigger.update(value => value + 1);
      }
      this.previousUnread = summary.unread;
    });
  }

  ngOnInit(): void {
    void this.state.initialize();
  }

  badgeValue(unread: number): string {
    return unread > MAX_DISPLAYED_BADGE_COUNT ? `${MAX_DISPLAYED_BADGE_COUNT}+` : `${unread}`;
  }
}
